import { Flex, ProgressBar, View } from '@adobe/react-spectrum';
import { useRef } from 'react';
import { ItemCell } from './components/ItemCell';
import styles from './FPATree.module.css';
import { FPAData, FPADataTypes } from '../../../infra/protected/FPA/FPAData';
import { useSelector } from 'react-redux';
import { RootState } from '../../../state/store';
import { useContainerWidth } from './hooks/useContainerWidth';
import { useDynamicIndentation } from './hooks/useDynamicIndentation';

export interface IFPATreeProps {
  treeData: FPAData[];
  onItemOpened?: (item: FPAData) => void;
  onItemClosed?: (item: FPAData) => void;
  onItemSelected?: (item: FPAData, show_context:boolean) => void;
  onItemSearching?: (search:string, parent_id?:number, parent_type?:FPADataTypes, is_page?:boolean) => void;
  onItemSearchOpened?: (item: FPAData) => void;
  onItemSearchClosed?: (isCancel:boolean) => void;
  onItemSearchReset?: (item: FPAData) => void;
  onSearchedItemSelected?: (item: FPAData) => void;
  onItemLoadMore?: (item: FPAData) => void;
}


export function FPATree ({
  treeData,
  onItemOpened,
  onItemClosed,
  onItemSelected,
  onItemSearching,
  onItemSearchOpened,
  onItemSearchClosed,
  onItemSearchReset,
  onSearchedItemSelected,
  onItemLoadMore
}: IFPATreeProps) {

  const searchParent = useSelector((state: RootState) => state.finder.searchParent);
  const lastOpenedItem = useSelector((state: RootState) => state.finder.lastOpenedItem);
  const isLoading = useSelector((state: RootState) => state.finder.isLoading);
  const loadingIndex = useSelector((state: RootState) => state.finder.loadingIndex);

  // Container ref for width monitoring
  const containerRef = useRef<HTMLSpanElement>(null);
  
  // Monitor container width
  const containerWidth = useContainerWidth(containerRef as React.RefObject<HTMLElement>);
  
  // Calculate dynamic indentation based on depth and width
  const { indentValue, isCompactMode } = useDynamicIndentation(treeData, containerWidth);

  const handleItemClicked = (item: FPAData, show_context: boolean) => {
    if(onItemSelected) onItemSelected(item, show_context);
  }

  const handleSelectedSearchItem = (item: FPAData, search_item:any) => {
    if(onSearchedItemSelected) onSearchedItemSelected(search_item.item);
  }

  const searchCallback = (search:string, parent_id?:number, parent_type?:FPADataTypes):void => {
    if(onItemSearching) onItemSearching(search, parent_id, parent_type);
  };

  const handleShowOverlay = (status:boolean, item: FPAData, isCancel:boolean) => {
    if(status){
      if(onItemSearchOpened) onItemSearchOpened(item);
    }else{
      if(onItemSearchClosed) onItemSearchClosed(isCancel);
    }
  }

  const handleResetSearch = (item:FPAData) => {
    // console.log('FPATree.handleResetSearch');
    onItemSearchReset && onItemSearchReset(item);
  }

  const loadChildData = (item:FPAData) => {
    if(item.isOpen){
      if(onItemClosed) onItemClosed(item);
    } else {
      if(onItemOpened) onItemOpened(item);
    }
  }

  const handleOnScroll = (e: React.UIEvent<HTMLSpanElement>) => {
    if(e.currentTarget.scrollHeight - e.currentTarget.scrollTop === e.currentTarget.clientHeight){
      if(lastOpenedItem && lastOpenedItem.isOpen && !lastOpenedItem.isFiltered && lastOpenedItem.page_offset < lastOpenedItem.count){
        onItemLoadMore && onItemLoadMore(lastOpenedItem);
      } 
    }
  }
  // Apply dynamic CSS variables and compact mode class
  const containerClassName = `${styles.container} ${isCompactMode ? styles.compactMode : ''}`;
  const containerStyle = {
    '--dynamic-indent-base': `${indentValue}px`,
    '--dynamic-indent-level': `${indentValue}px`,
    '--compact-mode': isCompactMode ? '1' : '0',
  } as React.CSSProperties;

  const showLoadMorePlaceholder =
    lastOpenedItem &&
    lastOpenedItem.isOpen &&
    !lastOpenedItem.isFiltered &&
    lastOpenedItem.page_offset < lastOpenedItem.count &&
    !isLoading;

  const lastChildIndex = showLoadMorePlaceholder
    ? treeData.reduce(
        (idx, item, i) =>
          item.parent_id === lastOpenedItem?.id ? i : idx,
        -1
      )
    : -1;

  const placeholderIndentLevel = Math.max(
    0,
    (lastOpenedItem?.node_level ?? -1)
  );

  const loadMorePlaceholder = showLoadMorePlaceholder &&
    lastChildIndex >= 0 && (
      <Flex
        key="load-more-placeholder"
        direction="row"
        alignItems="center"
        UNSAFE_className={styles.loadMorePlaceholder}
      >
        <View
          width={`calc(var(--dynamic-indent-base, var(--tree-indent-base)) + var(--dynamic-indent-level, var(--tree-indent-level)) * ${placeholderIndentLevel})`}
        />
        <Flex
          direction="row"
          flex
          alignItems="center"
          gap="size-100"
          UNSAFE_className={styles.treeItemSkeleton}
        >
          <div className={styles.skeletonIcon} aria-hidden />
          <Flex direction="column" flex gap="size-50" UNSAFE_style={{ minWidth: 0, marginLeft: 10 }}>
            <div className={styles.skeletonBar} style={{ width: '60%' }} />
            <div className={styles.skeletonBar} style={{ width: '45%' }} />
            <div className={styles.skeletonBar} style={{ width: '30%' }} />
          </Flex>
          <div className={styles.skeletonStatus} aria-hidden />
        </Flex>
      </Flex>
    );

  return (
    <>
      <span
        ref={containerRef}
        className={containerClassName}
        onScroll={handleOnScroll}
        style={containerStyle}
        data-compact-mode={isCompactMode ? 'true' : 'false'}
      >
        <Flex direction="column" gap="size-100" UNSAFE_className={styles.treeHolder}>
          <View>
            {treeData.map((item, index) => {
              const loading_bar = (
                <View
                  key={`loading_${index}`}
                  backgroundColor="static-white"
                  padding="size-100"
                  borderRadius={'small'}
                >
                  <ProgressBar
                    label={'Loading...'}
                    labelPosition="side"
                    isIndeterminate
                  />
                </View>
              );
              const item_cell = (
                <ItemCell
                  key={`${item.id}_${item.isOpen}_${item.isFiltered}_${item.isSelected}_${item.isSearching}`}
                  item={item}
                  loadChildData={loadChildData}
                  onResetSearch={handleResetSearch}
                  isSearched={searchParent === item}
                  onItemClicked={handleItemClicked}
                  onSelectSearchItem={handleSelectedSearchItem}
                  searchCallback={searchCallback}
                  showOverlay={handleShowOverlay}
                />
              );
              if (index === loadingIndex && isLoading) {
                return [item_cell, loading_bar];
              }
              if (index === lastChildIndex && loadMorePlaceholder) {
                return [item_cell, loadMorePlaceholder];
              }
              return item_cell;
            })}
          </View>
        </Flex>
      </span>
    </>
  );
}
